import React, { useEffect, useRef, useState } from 'react'
import Input from '../../components/input/Input';
import { toast } from 'react-toastify';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CopyToClipboard from 'react-copy-to-clipboard';
import useManageOrder from '../order/useManageOrder';
import './OrderForm.css'
import { validateNumberInput, numberFields } from '../../utils/formValidations';
import GetWebsiteService from '../../services/GetWebsiteService';
import OrderService from '../order/OrderService';
import { formatAmountRound } from '../../utils/formatAmount';
import { generateBlacklistWarningMessage, getShopDisplayName } from '../../utils/shopNameMapping';
import { selectKeyDataContact, setKeyDataContact } from '../../redux/features/keyDataSlice';
import { useDispatch, useSelector } from 'react-redux';
import ShippingDetails from '../../sharedComponents/ShippingDetails';
import { useAuth } from '../../context/AuthContext';
import useCustomerProfile from '../customerProfile/useCustomerProfile';
import { selectRateByShop, selectStandardRate } from '../../redux/features/todayRateSlice';
// Type selection removed - expert admin will assign via hand-carry admin page
import ConfirmImageModal from './ConfirmImageModal';
import BlacklistWarningModal from '../../sharedComponents/BlacklistWarningModal';
import { FormControl, InputLabel, Select, MenuItem, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Backdrop, CircularProgress, Portal } from '@mui/material';
import ShippingTypeDialog from '../../sharedComponents/ShippingTypeDialog';
import IsAirUrlService from '../backoffice/isAirUrl/IsAirUrlService';
import OrderByOptionService from '../setting/orderByOption/OrderByOptionService';
import OrderByMercariNateDailyUsageLabel from '../../sharedComponents/OrderByMercariNateDailyUsageLabel';
import useRoutingSuggestion from '../../hooks/useRoutingSuggestion';
import { extractItemNoFromUrl } from '../../utils/postcodeExtractor';
import OrdererConfirmModal from '../../sharedComponents/OrdererConfirmModal';
import { showsBillUrl, showsTransactionUrl } from '../../utils/websiteLinkFields';

const orderInitialState = {
	url: '',
	amount: '',
	deliveryFeeJp: null,
	orderDate: new Date(),
	orderByOptionId: null,
	shipDetail: 'normal',
	qty : 1,
	isCredit:false,
	isPre:false,
	isDown: false,
	isSeven:false,
	isHandCarry: false,
	isNoTracking: false,
	airItemType: '',
	billUrl: '',
	transactionPageUrl: '',
	suggestedOrderByOptionId: null,
	suggestedWarehouseReceivingAddressId: null,
}

const OrderForm = ({ profile, setProfile, getProfileFromContact, onActionComplete }) => {

	const dispatch = useDispatch()
	const { authState } = useAuth();
	const userInputRef = useRef(false);
	const isUserInputContactRef = useRef(false);
	const userInputContactValueRef = useRef('');
	const shownDialogForContactRef = useRef('');
	const pendingShippingDialogRef = useRef(false);
	const globalContact = useSelector(selectKeyDataContact);
	const [order, setOrder] = useState(orderInitialState);
	const [isButtonDisabled, setIsButtonDisabled] = useState(false);
	const shopName = useSelector((state) => state.shop.name);
	const todayRate = useSelector(state => selectRateByShop(state, shopName));
	const standardRate = useSelector(selectStandardRate);
const [showConfirmImageModal, setShowConfirmImageModal] = useState(false);
	const [showShippingTypeDialog, setShowShippingTypeDialog] = useState(false);
	const [lastContact, setLastContact] = useState('');
	const [blacklistWarningQueue, setBlacklistWarningQueue] = useState([]);
	const [currentBlacklistWarning, setCurrentBlacklistWarning] = useState(null);
	const [sameContactWarningMessage, setSameContactWarningMessage] = useState(null);
	const [postcodeWarning, setPostcodeWarning] = useState(null); // For showing postcode mismatch after save
	const isSubmittingRef = useRef(false); // Prevent duplicate order creation
	const [isLoadingItemData, setIsLoadingItemData] = useState(false); // Loading state for item data fetch

	const [isAirUrlDetected, setIsAirUrlDetected] = useState(false); // Show card when URL is in isAirUrl list; clear on submit
	const [orderByOptions, setOrderByOptions] = useState([]); // Order-by options from settings table
	const [anshinSuggestion, setAnshinSuggestion] = useState(null); // Anshin appraisal available for this URL (amountJpy 0 = free)
	const [addAnshinFee, setAddAnshinFee] = useState(false); // Checked = we will send this item for anshin appraisal
	const [pendingOrdererMatch, setPendingOrdererMatch] = useState(null);
	const [isCheckingOrderer, setIsCheckingOrderer] = useState(false);
	const { routingSuggestion, fetchRoutingSuggestion, clearRoutingSuggestion } = useRoutingSuggestion();

	const {
		error,
		addNewOrder
	} = useManageOrder()

	// Ask the backend whether this URL supports anshin appraisal (amountJpy 0 = free).
	// Debounced so we don't query on every keystroke.
	useEffect(() => {
		setAnshinSuggestion(null);
		setAddAnshinFee(false);
		const url = (order?.url || '').trim();
		if (!/(jp\.mercari\.com|mercari-shops\.com)/i.test(url)) return;
		const timer = setTimeout(() => {
			OrderService.suggestExtraFees(url)
				.then(res => setAnshinSuggestion(res.data?.suggestions?.[0] || null))
				.catch(() => setAnshinSuggestion(null));
		}, 600);
		return () => clearTimeout(timer);
	}, [order?.url]);

	const calculateFlowRate = () => {
		if (!standardRate || !todayRate) return 0;
		const percentageAdjustment = (todayRate / standardRate) - (profile?.extraPercentDiscount || 0);
		const newRate = standardRate * percentageAdjustment - (profile?.extraFlowDiscount || 0);

		return Math.round(newRate * 10000) / 10000;
	};

	const handleCloseBlacklistWarning = () => {
		setBlacklistWarningQueue(prev => {
			const remaining = prev.slice(1);
			if (remaining.length > 0) {
				setCurrentBlacklistWarning(remaining[0]);
			} else {
				setCurrentBlacklistWarning(null);
			}
			return remaining;
		});
	};

	const handleCloseSameContactWarning = () => {
		setSameContactWarningMessage(null);
		// After same contact warning is closed, show shipping dialog if it was pending
		if (pendingShippingDialogRef.current) {
			setShowShippingTypeDialog(true);
			pendingShippingDialogRef.current = false;
		}
	};

	const handleProfileChange = (name, value) => {
		if (numberFields.has(name)) {
			value = validateNumberInput(value, order[name]);
		}
		const previousValue = name === 'contact' ? (profile?.contact?.trim() || '') : null;
		
		if (name === 'contact') {
			const trimmedValue = value?.trim() || '';
			const previousTrimmed = previousValue?.trim() || '';
			
			// If contact is cleared, reset profile state completely
			if (!trimmedValue) {
				// When contact is deleted/cleared, reset all flags and profile state
				setProfile({ contact: '' });
				userInputRef.current = false;
				isUserInputContactRef.current = false;
				userInputContactValueRef.current = '';
				shownDialogForContactRef.current = '';
				setSameContactWarningMessage(null);
				return;
			}
			
			// Update profile with new contact value
			setProfile(prevState => ({
				...prevState,
				[name]: value
			}));
			
			// Mark as user input when:
			// 1. Contact goes from empty to non-empty (fresh paste/type), or
			// 2. Contact is replaced with a different value (e.g. paste new name over "abc")
			const isFreshInput = !previousTrimmed && trimmedValue;
			const isReplaceInput = previousTrimmed && trimmedValue && previousTrimmed !== trimmedValue;
			
			if (isFreshInput || isReplaceInput) {
				userInputRef.current = true;
				isUserInputContactRef.current = true; // Mark as user input
				userInputContactValueRef.current = trimmedValue; // Store the contact value at input time
				if (isReplaceInput) {
					// Allow shipping dialog to show again for the new contact
					shownDialogForContactRef.current = '';
				}
			} else {
				// If it's a small edit (same base contact), keep the original value in refs
				userInputRef.current = true;
			}
		} else {
			// For non-contact fields, update normally
			setProfile(prevState => ({
				...prevState,
				[name]: value
			}));
		}
	};

  // A buyer the bot can lock a card to: switched on in the Dispatch Bot page, with a LINE channel.
  const canLockCard = (buyerId) => {
    const b = orderByOptions.find((o) => o.id === buyerId);
    return Boolean(b && b.dispatchEnabled && (b.dispatchLineId || b.lineGroupId));
  };

  const handleOrderChange = (name, value) => {
		if (numberFields.has(name)) {
			value = validateNumberInput(value, order[name]);
		}
		// This is to handle checkbox values
		if (typeof value === 'boolean') {
			setOrder(prevState => ({
				...prevState,
				[name]: value
			}));
			return;
		}
		setOrder(prevState => ({
			...prevState,
			[name]: value
		}));
	};

  const handleDateChange = (name, selectedDate) => {
    setOrder(prevOrder => ({
        ...prevOrder,
        [name]: selectedDate
    }));
	};

	const handleShippingTypeSelect = (type) => {
		if (type === 'Air') {
			handleOrderChange('isHandCarry', true);
		} else if (type === 'Ship') {
			handleOrderChange('isHandCarry', false);
		}
		setShowShippingTypeDialog(false);
	};

	const handleShippingTypeCancel = () => {
		// Reset profile state and contact data - explicitly clear contact
		setProfile({ contact: '' });
		dispatch(setKeyDataContact(""));
		setLastContact('');
		setShowShippingTypeDialog(false);
		isUserInputContactRef.current = false;
		userInputContactValueRef.current = '';
		shownDialogForContactRef.current = '';
		setSameContactWarningMessage(null);
		// Reset isHandCarry
		setOrder(prevOrder => ({
			...prevOrder,
			isHandCarry: false
		}));
	};

	const handleOpenProfile = () => {
		if (profile && profile.smileName) {
			const url = `/customer-profile?smileName=${encodeURIComponent(profile.smileName)}`;
			window.open(url, '_blank');
		} else {
			toast.error('ใส่ชื่อลูกค้าก่อน');
		}
	}

	const handleButtonClick = (type) => {
		let newDetail = type;
		let newFee = null;
		
		if (order.shipDetail === type) {
				newDetail = 'normal';
		}

		if (type === 'free') {
				newFee = '0';
		}

		setOrder(prevOrder => ({ 
				...prevOrder,
				shipDetail: newDetail,
				deliveryFeeJp: newFee
		}));
	};

	const handleSave = async () => {
		setIsButtonDisabled(true);

		// Mercari / Mercari Shop: the dispatch bot finds the buyer, so Order By may stay empty
		// (a buyer's proof fills it in). Every other website still needs one.
		const botWebsite = /mercari/i.test(order.website || '');
		if (!order.orderByOptionId && !botWebsite) {
			toast.warning('กรุณาเลือก Order By ก่อนบันทึก');
			setIsButtonDisabled(false);
			return;
		}

		if(order.deliveryFeeJp == 0 && order.shipDetail == 'normal'){
			toast.warning('Please select Shipping Detail')
		}else if(order.isSeven && order.sevenFee == null){
			toast.warning('Please enter Seven Fee')
		}else if(botWebsite){
			// Nobody has bought it yet (the bot or FujiKing will): no proof to ask for — save straight away.
			await handleFinalSave(null, null);
			return;
		}else{
			// Show confirmation image modal first
			setShowConfirmImageModal(true);
			setIsButtonDisabled(false);
			return;
		}
		setIsButtonDisabled(false);
	};

	const handleFinalSave = async (confirmImage = null, validationResult = null) => {
		// Prevent duplicate submission
		if (isSubmittingRef.current) {
			console.warn('Order submission already in progress, ignoring duplicate call');
			return;
		}

		isSubmittingRef.current = true;
		setIsButtonDisabled(true);
		
		// Note: airItemType selection is now optional - expert will assign later
		
		// Check and correct the rate before saving
		if (profile?.isFlowRate && Math.abs(profile.productRate - 0.29) < 0.0001) {
			const correctRate = calculateFlowRate();
			setProfile(prevProfile => ({
				...prevProfile,
				productRate: correctRate
			}));
			
			await new Promise(resolve => setTimeout(resolve, 0));
		}

		// Store validation result BEFORE upload (validation is already completed at this point)
		// This ensures we have the result even if upload completes quickly
		const storedValidationResult = validationResult;

		// Create FormData for file upload
		const formData = new FormData();
		
		// Add the image file if provided
		if (confirmImage) {
			formData.append('images', confirmImage);
		}
		
		// Add other data as JSON string. Checked ส่งตรวจ goes WITH the order (owner 2026-11-01): the
		// dispatch bot sends the buyer's card right after the save, and the card must already say
		// อันชิน — a second request after the create came seconds too late.
		// Mercari (owner 2026-11-02): Order By IS the pin — the bot's card goes to that buyer only. A buyer
		// who cannot take a card (bot off / no LINE) was already warned about on pick; the queue runs then.
		const orderOut = { ...order, dispatchPinnedBuyerId: /mercari/i.test(order.website || '') && canLockCard(order.orderByOptionId) ? order.orderByOptionId : null };
		const orderData = {
			profile: profile,
			order: addAnshinFee && anshinSuggestion
				? { ...orderOut, extraFees: [{ type: anshinSuggestion.type, label: anshinSuggestion.label, amountJpy: Number(anshinSuggestion.amountJpy) || 0 }] }
				: orderOut
		};
		formData.append('orderData', JSON.stringify(orderData));
		formData.append('userId', authState.user.id);

		try {
			setIsCheckingOrderer(true);
			const addResult = await addNewOrder(formData);
			setIsCheckingOrderer(false);
			if(addResult){
				if (addResult?.ordererMatch) {
					// Capture the assigned orderer now — order state is reset just below.
						setPendingOrdererMatch({ ...addResult.ordererMatch, assignedOrderByOptionId: order.orderByOptionId });
				}

				// Checked = we send this item for anshin appraisal: the service line went in with the
				// create (Mercari's fee, not editable) — 0 records a free ส่งตรวจ.
				if (addAnshinFee && anshinSuggestion) {
					const feeJpy = Number(anshinSuggestion.amountJpy) || 0;
					toast.success(feeJpy > 0
						? `บันทึกส่งตรวจ + ค่าบริการ ¥${feeJpy} เข้าออเดอร์แล้ว`
						: 'บันทึกส่งตรวจ (ฟรี) เข้าออเดอร์แล้ว');
				}
				setAnshinSuggestion(null)
				setAddAnshinFee(false)

				toast.success('Add New Order Successfully!!')
				setOrder(orderInitialState)
				setProfile({ contact: '' })
				onActionComplete()
				dispatch(setKeyDataContact(""))
				setShowConfirmImageModal(false)
				// Reset dialog state
				setLastContact('')
				setShowShippingTypeDialog(false)
				isUserInputContactRef.current = false
				userInputContactValueRef.current = ''
				shownDialogForContactRef.current = ''
				setSameContactWarningMessage(null)
			setIsAirUrlDetected(false)
			clearRoutingSuggestion()

		// Show image warning AFTER successful save
		const hasItemNoMismatch = storedValidationResult?.itemNoMismatch === true;
		const hasPostcodeMismatch = storedValidationResult && !storedValidationResult.isValid && !storedValidationResult.cannotValidate;
		const hasNameMismatch = storedValidationResult?.nameMismatchMessage;
		const orderUrlAtSave = order.url;
		if (hasItemNoMismatch || hasPostcodeMismatch || hasNameMismatch) {
			setTimeout(() => {
				setPostcodeWarning({
					...storedValidationResult,
					...(hasItemNoMismatch ? { expectedOrderUrl: orderUrlAtSave } : {}),
				});
			}, 100);
		}
			}else{
				toast.error(error)
			}
		} catch (error) {
			console.error('Error creating order:', error);
			toast.error(error?.message || 'Failed to create order');
		} finally {
			// Always reset submission flag and button state
			isSubmittingRef.current = false;
			setIsButtonDisabled(false);
			setIsCheckingOrderer(false);
		}
	};

	useEffect(() => {
		toast.error(error)
	},[error])

	useEffect(() => {
		const fetchOrderByOptions = async () => {
			try {
				const res = await OrderByOptionService.getActiveOrderByOptions();
				setOrderByOptions(res.data || []);
			} catch (err) {
				console.error('Failed to fetch order-by options:', err);
			}
		};
		fetchOrderByOptions();
	}, [])

	// Prefill from the pending-payment flow (NewOrder sets keyDataContact) — but only
	// when it CHANGES while the page is open. On mount the redux value can only be a
	// stale leftover from a previous visit: KeyData clears it in its own mount effect,
	// but child effects run FIRST, so applying it here re-filled the previous
	// contact every time an admin re-entered the page.
	const globalContactFirstRunRef = useRef(true);
	useEffect(() => {
		if (globalContactFirstRunRef.current) {
			globalContactFirstRunRef.current = false;
			return;
		}
		if (globalContact) {
			setProfile(prevProfile => ({
				...prevProfile,
				contact: globalContact
			}));
			userInputRef.current = true;
			// Don't mark as user input - this is from programmatic change
			isUserInputContactRef.current = false;
			userInputContactValueRef.current = '';
			shownDialogForContactRef.current = '';
		} else {
			// Cleared from outside (e.g. after "เก็บเงิน" charged the selected customer):
			// empty the field the same way deleting the contact by hand does.
			handleProfileChange('contact', '');
		}
	}, [globalContact]);

	// Mirrors the contact as of the LATEST render, so an async fetch can tell whether
	// its response is stale (admin pasted a new contact / submitted while in flight).
	const contactNowRef = useRef('');
	contactNowRef.current = profile?.contact ?? '';

	useEffect(() => {
		const fetchData = async () => {
			try {
				if (profile?.contact?.trim()) {
					const requestedContact = profile.contact;
					// applyToState=false: WE apply the profile below, only after confirming
					// the response isn't stale — the hook applying it directly resurrected
					// the previous customer over a newly pasted one (or over a cleared form).
					const response = await getProfileFromContact(requestedContact, false);
					if ((contactNowRef.current || '').trim() !== requestedContact.trim()) return; // stale — discard
					console.log('[OrderForm] Fetch profile for contact:', requestedContact, '→ productRate:', response?.productRate);
					if (!response) {
						toast.info("Profile not registered");
						setProfile({
							contact: profile.contact
						});
					} else {
						setProfile(response);
						// Check if blacklisted in other shops (higher priority)
						if (response.isBlacklistedInOtherShops && response.blacklistedShops && response.blacklistedShops.length > 0) {
							const warningMessages = response.blacklistedShops.map(blacklistedShop => {
								const formattedAmount = formatAmountRound(blacklistedShop.totalPendingAmount);
								return generateBlacklistWarningMessage(blacklistedShop.shop, formattedAmount, blacklistedShop.source);
							});
							setBlacklistWarningQueue(warningMessages);
							if (warningMessages.length > 0) {
								setCurrentBlacklistWarning(warningMessages[0]);
							}
							// Clear same contact warning if blacklist warning exists
							setSameContactWarningMessage(null);
						} else {
							// Check if has same contact in other shops (only if not blacklisted)
							// Only show warning for first order (smileOrderNo === 0)
							if (response.hasSameContactInOtherShops && response.smileOrderNo === 0) {
								// Get the other shop name (opposite of current shop)
								const otherShop = shopName === 'smile' ? 'tama' : 'smile';
								const otherShopDisplayName = getShopDisplayName(otherShop);
								setSameContactWarningMessage(`กรุณาไปตรวจสอบที่ ${otherShopDisplayName} ว่าเป็นคนเดียวกันไหม ถ้าคนละคน กดได้ แต่ถ้า คนเดียวกันห้ามกด`);
							} else {
								setSameContactWarningMessage(null);
							}
						}
					}
				}
			} catch (error) {
				console.log("Error fetching data:", error);
			}
			userInputRef.current = false;
		};

		if (userInputRef.current) {
			const timer = setTimeout(fetchData, 500);
			return () => clearTimeout(timer);
		}
	}, [profile?.contact, getProfileFromContact]);

	// Show shipping type dialog only when user types/pastes contact and profile is found
	useEffect(() => {
		const currentContact = profile?.contact?.trim() || '';
		// Reset lastContact and close dialog when contact becomes empty
		if (!currentContact) {
			setLastContact('');
			setShowShippingTypeDialog(false);
			isUserInputContactRef.current = false;
			userInputContactValueRef.current = '';
			shownDialogForContactRef.current = '';
			setSameContactWarningMessage(null);
			// Reset isHandCarry when contact is cleared
			setOrder(prevOrder => ({
				...prevOrder,
				isHandCarry: false
			}));
			return;
		}
		// Only show dialog when:
		// 1. Current contact is not empty (safety check)
		// 2. Current contact matches the contact value that was user input (original value)
		// 3. It was user input (typing/pasting)
		// 4. Profile is found (has smileName)
		// 5. We haven't shown the dialog for this contact yet
		const isProfileFound = profile?.smileName;
		const wasUserInput = isUserInputContactRef.current;
		const matchesUserInput = currentContact === userInputContactValueRef.current;
		const hasShownDialog = shownDialogForContactRef.current === userInputContactValueRef.current;
		
		// Additional safety: don't show dialog if contact is empty or if flags indicate deletion
		if (!currentContact || (!wasUserInput && !matchesUserInput)) {
			return;
		}
		
		// Only show dialog if current contact exactly matches the original user input value
		// This prevents dialog from showing when user edits the contact (adds/deletes chars)
		if (matchesUserInput && isProfileFound && wasUserInput && !hasShownDialog) {
			// Check if warning modal is currently showing or pending
			if (currentBlacklistWarning !== null || blacklistWarningQueue.length > 0 || sameContactWarningMessage !== null) {
				// Mark that we need to show shipping dialog after warnings are closed
				pendingShippingDialogRef.current = true;
				setLastContact(currentContact);
				shownDialogForContactRef.current = userInputContactValueRef.current; // Mark as shown for the original contact value
				// Clear the flag after marking as pending
				isUserInputContactRef.current = false;
				userInputContactValueRef.current = '';
			} else {
				// No warnings, show shipping dialog immediately
				setLastContact(currentContact);
				shownDialogForContactRef.current = userInputContactValueRef.current; // Mark as shown for the original contact value
				// Reset isHandCarry when contact changes
				setOrder(prevOrder => ({
					...prevOrder,
					isHandCarry: false
				}));
				setShowShippingTypeDialog(true);
				// Clear the flag after showing dialog
				isUserInputContactRef.current = false;
				userInputContactValueRef.current = '';
			}
		} else if (currentContact !== lastContact) {
			// Contact changed - update lastContact
			setLastContact(currentContact);
			// Only reset shown dialog tracking if this is a completely different contact
			// (not just an edit of the same contact)
			const isDifferentContact = !userInputContactValueRef.current || 
				!currentContact.startsWith(userInputContactValueRef.current) && 
				!userInputContactValueRef.current.startsWith(currentContact);
			
			if (isDifferentContact) {
				shownDialogForContactRef.current = ''; // Reset - allow dialog for new contact
				// Only clear flags if it was NOT user input (programmatic change)
				if (!wasUserInput || !matchesUserInput) {
					isUserInputContactRef.current = false;
					userInputContactValueRef.current = '';
				}
			}
			// If it's just an edit (same base contact), don't reset shownDialogForContactRef
			// This prevents dialog from showing again on edits
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [profile?.contact, profile?.smileName, currentBlacklistWarning, blacklistWarningQueue.length, sameContactWarningMessage]);

	useEffect(() => {
    if (!order.url) {
			setIsAirUrlDetected(false);
			return;
		}
		const postData = async () => {
				try {
					handleOrderChange('imageUrl',"")
					// A new link starts with no website: an unregistered link (or a failed
					// lookup) must not keep the previous link's name — and with it its fee.
					handleOrderChange('website', null)

					// Check isAirUrl list: if URL matches, show card and auto-select isHandCarry (Air)
					let isAirDetected = order.isHandCarry || false;
					try {
						const { isAirUrl } = await IsAirUrlService.checkByUrl(order.url);
						if (isAirUrl) {
							isAirDetected = true;
							setIsAirUrlDetected(true);
							handleOrderChange('isHandCarry', true);
						} else {
							setIsAirUrlDetected(false);
						}
					} catch (e) {
						setIsAirUrlDetected(false);
					}

					// Use merged endpoint: getWebsiteFromUrl + item data (if Mercari/Yahoo)
					setIsLoadingItemData(true);
					
					try {
						// This endpoint does:
						// 1. getWebsiteFromUrl (database lookup) - normal flow
						// 2. If website is Mercari/Yahoo, fetch item data
						// 3. Return both website and item data
						const response = await GetWebsiteService.getItemDataFromUrl(order.url);
						
						if (response.data.success && response.data.data) {
							const { website, mainImage, finalPrice, deliveryFee, shippingMethodOrderBlocked, title } = response.data.data;
							handleOrderChange('isNoTracking', shippingMethodOrderBlocked === true);
							// Capture the original item title for categorization (all sites)
							handleOrderChange('itemTitle', title || null);
							
							// Set website name (from database lookup or URL pattern detection)
							if (website) {
								handleOrderChange('website', website);
							} else if (website === "") {
								toast.warning("Website not register!!")
							}

						// Clear each link field when the (new) website shouldn't show it, so a
						// hidden field can't submit a stale value. Kept in sync with the inputs below.
						if (!showsBillUrl(website)) {
							handleOrderChange('billUrl', '');
						}
						if (!showsTransactionUrl(website)) {
							handleOrderChange('transactionPageUrl', '');
						}
							
							// Mercari or Mercari Shop – shared delivery-fee & price auto-fill logic
							if (website === 'Mercari' || website === 'Mercari Shop') {
								if (mainImage) {
									handleOrderChange('imageUrl', mainImage);
								} else if (website === 'Mercari') {
									const produtCode = order.url.substring(order.url.lastIndexOf("/") + 1);
									const imgUrl = 'https://static.mercdn.net/item/detail/orig/photos/' + produtCode + '_1.jpg'
									handleOrderChange('imageUrl', imgUrl);
								}
								
								if (finalPrice) {
									handleOrderChange('amount', finalPrice);
								}
								
								// Auto-fill deliveryFee:
								// - '0' = free shipping (seller pays)
								// - 'COD' = COD/unknown (buyer pays, admin needs to check)
								// - numeric string = known amount (buyer pays)
								if (deliveryFee === '0') {
									handleOrderChange('deliveryFeeJp', '0');
									handleOrderChange('shipDetail', 'free');
								} else if (deliveryFee === 'COD') {
									handleOrderChange('deliveryFeeJp', null);
									handleOrderChange('shipDetail', 'cod');
								} else if (deliveryFee && !isNaN(deliveryFee)) {
									handleOrderChange('deliveryFeeJp', deliveryFee);
									handleOrderChange('shipDetail', 'normal');
								}
						} else if (website && (website.includes('Yahoo') || website.includes('Auction'))) {
							if (mainImage) {
								handleOrderChange('imageUrl', mainImage);
							}
							if (finalPrice) {
								handleOrderChange('amount', finalPrice);
							}
							handleOrderChange('deliveryFeeJp', null);
							handleOrderChange('shipDetail', 'normal');
						} else if (website && website.includes('PayPay')) {
							if (mainImage) {
								handleOrderChange('imageUrl', mainImage);
							}
							if (finalPrice) {
								handleOrderChange('amount', finalPrice);
							}
							// PayPay Flea Market shipping is always free
							handleOrderChange('deliveryFeeJp', '0');
							handleOrderChange('shipDetail', 'free');
						} else if (website === 'Mercari' && !mainImage) {
								const produtCode = order.url.substring(order.url.lastIndexOf("/") + 1);
								const imgUrl = 'https://static.mercdn.net/item/detail/orig/photos/' + produtCode + '_1.jpg'
								handleOrderChange('imageUrl', imgUrl);
							}

							// Fetch routing suggestion using the fresh values from the API response.
							// Triggered here (not on state change) to avoid unnecessary re-fetches.
							if (finalPrice) {
								const suggestion = await fetchRoutingSuggestion({
									website: website || null,
									amount: parseFloat(finalPrice),
									isAir: isAirDetected,
									customerId: profile?.id || null,
									paymentMethod: order.isSeven ? 'sevenEleven' : 'any',
								});
							if (suggestion?.orderer?.orderByOption?.id) {
								setOrder(prev => ({
									...prev,
									// No auto-select: admin must choose manually.
									// Only store the suggested IDs for tracking/audit purposes.
									suggestedOrderByOptionId: suggestion.orderer.orderByOption.id,
									suggestedWarehouseReceivingAddressId: suggestion.warehouse?.id || null,
								}));
							}
							}
						}
					} catch (error) {
						console.error("Error fetching website and item data:", error);
						// Don't show error toast, just continue
					} finally {
						setIsLoadingItemData(false);
					}
				} catch (error) {
					console.error("Error sending POST request:", error);
					setIsLoadingItemData(false);
				}
			};
			postData();
  }, [order.url]);

	// Log suggestedWarehouse whenever routing suggestion changes
	useEffect(() => {
		console.log('[RoutingSuggestion] warehouse changed →', routingSuggestion?.warehouse ?? null, {
			isHandCarry: order.isHandCarry,
			isSeven: order.isSeven,
			amount: order.amount,
			website: order.website,
			customerId: profile?.id ?? null,
		});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [routingSuggestion?.warehouse]);

	// Re-fetch suggestion when isSeven toggles (only payment change that affects routing)
	useEffect(() => {
		const amount = parseFloat(order.amount);
		if (!amount || isNaN(amount)) return;
		fetchRoutingSuggestion({
			website: order.website || null,
			amount,
			isAir: order.isHandCarry || false,
			customerId: profile?.id || null,
			paymentMethod: order.isSeven ? 'sevenEleven' : 'any',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [order.isSeven]);

	// Re-fetch suggested warehouse when isHandCarry toggles (Air vs Ship affects warehouse)
	useEffect(() => {
		const amount = parseFloat(order.amount);
		if (!amount || isNaN(amount)) return;
		const isAir = order.isHandCarry || false;
		console.log('[isHandCarry effect] firing — isAir sent to API:', isAir, '| order.isHandCarry:', order.isHandCarry);
		fetchRoutingSuggestion({
			website: order.website || null,
			amount,
			isAir,
			customerId: profile?.id || null,
			paymentMethod: order.isSeven ? 'sevenEleven' : 'any',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [order.isHandCarry]);

	// Re-fetch suggestion when customer changes (new profile loaded) and amount already known
	useEffect(() => {
		if (!profile?.id) return;
		const amount = parseFloat(order.amount);
		if (!amount || isNaN(amount)) return;
		fetchRoutingSuggestion({
			website: order.website || null,
			amount,
			isAir: order.isHandCarry || false,
			customerId: profile.id,
			paymentMethod: order.isSeven ? 'sevenEleven' : 'any',
		}).then(suggestion => {
			if (!suggestion?.orderer?.orderByOption?.id) return;
			// No auto-select — only update tracking fields
			setOrder(prev => ({
				...prev,
				suggestedOrderByOptionId: suggestion.orderer.orderByOption.id,
				suggestedWarehouseReceivingAddressId: suggestion.warehouse?.id || null,
			}));
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [profile?.id]);

	useEffect(() => {
		if (profile?.isFlowRate && profile?.extraFlowDiscount !== undefined) {
			const newRate = calculateFlowRate(); // Use the same helper function
			if (profile.productRate !== newRate) {
				setProfile((prevState) => ({
					...prevState,
					productRate: newRate,
				}));
			}
		}
	}, [todayRate, standardRate, profile]);

  return (
    <div className='border-b pb-4'>
			<div className='grid grid-cols-12 gap-x-6 gap-y-3 grid-flow-dense'>
				{isAirUrlDetected && (
					<div className='col-span-12 rounded-lg border-2 border-sky-500 bg-sky-50 px-4 py-3 flex items-center gap-3'>
						<span className='text-2xl' aria-hidden>✈️</span>
						<div>
							<p className='font-semibold text-sky-900'>รายการนี้เป็น แอร์ (Air)</p>
							<p className='text-sm text-sky-700'>ระบบเลือกหิ้วแอร์ให้แล้ว — ตรวจสอบและกดบันทึกได้</p>
						</div>
					</div>
				)}
			{routingSuggestion && routingSuggestion.suggestionVisible && (
				<div className="col-span-12 rounded-lg border-2 border-indigo-400 bg-indigo-50 px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-1">
					<span className="text-base font-semibold text-indigo-800">
						ระบบแนะนำ:
					</span>
				{routingSuggestion.orderer ? (
					<span className="text-sm text-indigo-700">
						ผู้สั่ง: <strong>{routingSuggestion.orderer.orderByOption?.label}</strong>
						{routingSuggestion.suggestedPaymentMethod === 'sevenEleven' && (
							<span className="ml-2 inline-block rounded bg-blue-600 px-2 py-0.5 text-xs font-bold text-white">จ่าย 7-Eleven</span>
						)}
						{routingSuggestion.suggestedPaymentMethod === 'merpay' && (
							<span className="ml-2 inline-block rounded bg-green-600 px-2 py-0.5 text-xs font-bold text-white">จ่าย Merpay</span>
						)}
						{routingSuggestion.orderer.isSoftCap && (
							<span className="ml-1 text-orange-600 font-semibold">[soft cap]</span>
						)}
					</span>
				) : (
						<span className="text-sm text-red-600 font-semibold">ไม่มีผู้สั่งที่รองรับได้วันนี้</span>
					)}
					{routingSuggestion.warehouse ? (
						<span className="text-sm text-indigo-700">
							โกดัง: <strong>{routingSuggestion.warehouse.addressNickName}</strong>
						</span>
					) : null}
					{routingSuggestion.orderer?.isOverflow && (
						<span className="text-xs text-orange-600 font-semibold">[เกินลิมิต — ไม่มีตัวเลือกอื่น]</span>
					)}
					{routingSuggestion.orderer?.contactInfo && (
						<span className="text-xs text-gray-500">Line: {routingSuggestion.orderer.contactInfo}</span>
					)}
					{routingSuggestion.urgencyInfo && (
						<span className={`text-xs font-medium ${
							routingSuggestion.urgencyInfo.merpayRemainingPct <= 30
								? 'text-red-600'
								: routingSuggestion.urgencyInfo.merpayRemainingPct <= 60
								? 'text-orange-500'
								: 'text-green-600'
						}`}>
						</span>
					)}
					{routingSuggestion.orderer && routingSuggestion.orderer.orderByOption?.orderByType !== 'mercariAccount' && (
						<button
							type="button"
							className="ml-auto text-xs bg-indigo-600 text-white rounded px-2 py-1 hover:bg-indigo-700 whitespace-nowrap"
							onClick={() => {
								const shopPrefix = shopName === 'smile' ? 'Smile' : 'Tama';
								const location = routingSuggestion.warehouse?.addressNickName ?? '';
								const msg = `${order.url}\n${shopPrefix} ${location}`.trim();
								navigator.clipboard.writeText(msg);
								toast.success(`Copy ส่ง ${routingSuggestion.orderer.orderByOption?.label} แล้ว`);
							}}
						>
							Copy ส่ง {routingSuggestion.orderer.orderByOption?.label}
						</button>
					)}
				</div>
			)}
				<div className='w-full col-span-3'>
					<div className='flex justify-between items-center'>
						<label htmlFor="contact" className="pl-1 text-sm font-medium leading-6 text-gray-900">
							Facebook/Line
						</label>
						<button 
							type="button"
							className="bg-red-500 text-white rounded-md hover:bg-red-600 btn-sm text-sm p-1 px-2"
							onClick={handleOpenProfile}
						>
							Profile
						</button>
					</div>
					<div className='w-full flex rounded-md shadow-sm'>
						<input
							type="text"
							name='contact'
							id='contact'
							value={profile?.contact}
							onChange={(e) => handleProfileChange('contact', e.target.value)}
							className={`w-full pl-2 block flex-1 border border-gray-300 py-1.5 rounded-md sm:text-sm sm:leading-6 text-red-900 ${profile?.Blacklists  && profile?.Blacklists.length> 0 ? 'bg-red-200' : ''}`}
						/>
					</div>
				</div>
				{/* <Input columnSpan={3} title={'Facebook/Line'} name={'contact'} value={profile?.contact} onChange={value => handleProfileChange('contact',value)}/> */}
				<div className='col-span-3'>
					<CopyToClipboard 
					text={profile?.smileName + (profile?.smileOrderNo + 1)}
					onCopy={() => toast.success(`${profile?.smileName + (profile?.smileOrderNo + 1)} - copied to clipboard!`)}
					>
					<div>
							<Input 
									title={shopName === 'tama' ? 'Tama Name' : 'Smile Name'} 
									name={'smileName'} 
									value={profile?.smileName + (profile?.smileOrderNo + 1)} 
									onChange={value => handleProfileChange('smileName', value)} 
									readonly={true}
							/>
					</div>
					</CopyToClipboard>
				</div>
				<div className='col-span-3'>
					<CopyToClipboard 
							text={profile?.smileNameJp + (profile?.smileOrderNo + 1)}
							onCopy={() => toast.success(`${profile?.smileNameJp + (profile?.smileOrderNo + 1)} - copied to clipboard!`)}
					>
					<div>
							<Input 
									title={shopName === 'tama' ? 'Tama Name Jp' : 'Smile Name Jp'} 
									name={'smileNameJp'} 
									value={profile?.smileNameJp + (profile?.smileOrderNo + 1)} 
									onChange={value => handleProfileChange('smileNameJp', value)} 
									readonly={true}
							/>
					</div>
					</CopyToClipboard>
				</div>
				<Input
					columnSpan={3}
					title={'Product Rate'}
					name={'productRate'}
					value={profile?.isFlowRate ? calculateFlowRate() : profile?.productRate}
					onChange={value => handleProfileChange('productRate', value)}
				/>
				<div className="col-span-3">
						<label htmlFor="orderDate" className="pl-1 flex text-sm font-medium leading-6 text-gray-900 items-center">
								Order Date
						</label>
						<div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 sm:max-w-md">
							<DatePicker 
								selected={order.orderDate}
								onChange={(date) => handleDateChange("orderDate", date)}
								dateFormat={"yyyy-MM-dd"}
								className='pl-2 w-full block border-0 bg-transparent py-1.5 pl-1 text-gray-900 placeholder:text-gray-400  sm:text-sm sm:leading-6'
							/>
						</div>
				</div>
				<Input
					columnSpan={3}
					title={'Link'}
					name={'url'}
					value={order?.url}
					rightText={isLoadingItemData ? 'Loading...' : order?.website}
					onChange={value => handleOrderChange('url',value)}
				/>
			<Input columnSpan={3} title={'Image Link'} name={'imageUrl'} value={order?.imageUrl} onChange={value => handleOrderChange('imageUrl',value)}/>
			<div className='col-span-3 row-span-3 mx-2'>
					<img className='h-44 w-80 object-contain' src={order.imageUrl} />
			</div>
		{showsBillUrl(order?.website) && (
			<Input columnSpan={3} title={'Bill URL'} name={'billUrl'} value={order?.billUrl} onChange={value => handleOrderChange('billUrl', value)}/>
		)}
		{showsTransactionUrl(order?.website) && (
			<Input columnSpan={3} title={'Transaction Page'} name={'transactionPageUrl'} value={order?.transactionPageUrl} onChange={value => handleOrderChange('transactionPageUrl', value)}/>
		)}
		{/* Fill the rest of the row so the fields below stay aligned:
		    6 cols when only Transaction shows, 3 cols when both Bill + Transaction show. */}
		{showsTransactionUrl(order?.website) && (
			<div className={showsBillUrl(order?.website) ? 'col-span-3' : 'col-span-6'} />
		)}
		<Input columnSpan={2} title={'Amount'} name={'amount'} value={order?.amount} onChange={value => handleOrderChange('amount',value)}/>
				<Input columnSpan={1} title={'Qty'} name={'qty'} value={order?.qty} onChange={value => handleOrderChange('qty',value)}/>
				<Input 
					columnSpan={shopName === 'tama' ? 2 : 3} 
					title={'Shipping Fee'} 
					name={'deliveryFeeJp'} 
					value={order?.deliveryFeeJp} 
					textColor={!['normal','free'].includes(order?.shipDetail) ? 'gray' : 'red'}
					readonly={order?.shipDetail !== 'normal'}
					onChange={value => handleOrderChange('deliveryFeeJp',value)}/>
				{	shopName === 'tama' && 
					<Input columnSpan={1} title={'ค่าบริการ'} name={'transactionFee'} value={profile?.transactionFee} onChange={value => handleOrderChange('transactionFee',value)} readonly={true}/>
				}
				<div className='col-span-3'>
					<div className='grid grid-cols-3 gap-2 mt-1'>
						<ShippingDetails order={order} onButtonClick={handleButtonClick}/>
					</div>
				</div> 
				<div className='col-span-3 grid grid-cols-3 h-6 items-center mt-1 item-center'>
					<div className='col-span-1'>
						<input
							id="down"
							name="isDown"
							type="checkbox"
							checked={order?.isDown}
							onChange={(e) => handleOrderChange('isDown', e.target.checked)}
							className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
						/>
						<label htmlFor="down" className="pl-2 pr-1 text-gray-900">
							มัดจำ
						</label>
						{order.isDown && profile && <span>({profile?.downRate}%)</span>}
					</div>
					<div className='col-span-1'>
						<input
							id="isHandCarry"
							name="isHandCarry"
							type="checkbox"
							checked={order.isHandCarry}
							onChange={(e) => handleOrderChange('isHandCarry', e.target.checked)}
							className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
						/>
						<label htmlFor="isHandCarry" className="pl-2 pr-1 text-gray-900">
							รายการหิ้ว
						</label>
					</div>
					<div className='col-span-1'>
						<input
							id="isCredit"
							name="isCredit"
							type="checkbox"
							checked={order.isCredit}
							onChange={(e) => handleOrderChange('isCredit', e.target.checked)}
							className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
						/>
						<label htmlFor="isCredit" className="pl-2 pr-1 text-gray-900">
							จ่ายบัตร (3.5%)
						</label>
					</div>
				</div>
				<div className='col-span-3 flex h-6 items-center mt-1'>
					<input
						id="isSeven"
						name="isSeven"
						type="checkbox"
						checked={order.isSeven}
    				onChange={(e) => handleOrderChange('isSeven', e.target.checked)}
						className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
					/>
					<label htmlFor="seven" className="pl-2 pr-1 text-gray-900 w-1/3">
						จ่าย Seven
					</label>
					{order.isSeven &&
						<div>
							<input
								type="text"
								name="sevenFee"
								id="sevenFee"
								value={order?.sevenFee}
								onChange={(e) => handleOrderChange('sevenFee', e.target.value)}
								className="w-2/3 pl-2 flex-1 border-2 border-gray-200 py-1 pl-1 rounded-md"
							/>
							<span className='pl-3 w-1/3'>เยน</span>
						</div>
					}
				</div>
				<div className='col-span-2 flex '>
					<input
						id="isPre"
						name="isPre"
						type="checkbox"
						checked={order.isPre}
						onChange={(e) => handleOrderChange('isPre', e.target.checked)}
						className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 my-auto"
					/>
					<label htmlFor="isPre" className="pl-2 pr-1 text-gray-900 my-auto">
						Pre-Order
					</label>
					{order.isPre && 
					<span className='ml-3 w-auto border-2 border-gray-200 rounded-md focus:ring-indigo-600 my-auto'>
							<span>
								<DatePicker 
									selected={order.preDate}
									onChange={(date) => handleDateChange("preDate", date)}
									dateFormat={"yyyy-MM-dd"}
									className='pl-2 w-full block border-0 bg-transparent py-1 pl-1 text-gray-900 placeholder:text-gray-400  sm:text-sm sm:leading-6'
								/>
							</span>
						</span>
					}
				</div>
				<div className="col-span-1 mt-auto ml-auto">
						<button
								onClick={handleSave}
								className="rounded-md bg-indigo-600 px-10 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
								disabled={isButtonDisabled}
								>
								Save
						</button>
				</div>
				<div className="col-span-3 col-start-1 flex gap-2 flex-wrap items-end">
					<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
						<FormControl size="small" sx={{ minWidth: 160 }}>
							<InputLabel id="order-by-label" shrink>Order By</InputLabel>
							<Select
								labelId="order-by-label"
								id="orderBy"
								value={
									order.orderByOptionId && orderByOptions.some((o) => o.id === order.orderByOptionId)
										? order.orderByOptionId
										: ''
								}
								label="Order By"
								notched
								displayEmpty
								onChange={(e) => handleOrderChange('orderByOptionId', e.target.value ? parseInt(e.target.value, 10) : null)}
							>
							<MenuItem value="" disabled>
								<em>กรุณาเลือก</em>
							</MenuItem>
								{orderByOptions.map((opt) => (
									<MenuItem key={opt.id} value={opt.id}>
										{opt.label}
										{order.suggestedOrderByOptionId === opt.id && (
											<Typography variant="caption" color="primary" sx={{ ml: 0.5 }}>✓</Typography>
										)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{/* Mercari: Order By is the pin (owner 2026-11-02). Blue = the card goes to this buyer only;
						    red = this buyer cannot take a card (bot off / no LINE), the bot queues it instead. */}
						{/mercari/i.test(order.website || '') && order.orderByOptionId && (
							canLockCard(order.orderByOptionId)
								? <Typography variant="caption" color="primary" sx={{ mt: 0.5 }}>🔒 การ์ดจะส่งให้คนนี้เท่านั้น</Typography>
								: <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>ล็อกการ์ดให้คนนี้ไม่ได้ (ไม่ได้เปิดรับงานบอท หรือไม่มี LINE) — บอทจะส่งตามคิว</Typography>
						)}
					</Box>
				</div>
				<div className='col-span-3'>
					{anshinSuggestion && (
						<div className='flex items-center gap-2 flex-wrap'>
							<label className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border-2 cursor-pointer ${
								Number(anshinSuggestion.amountJpy) > 0
									? 'border-amber-400 bg-amber-50 text-amber-800'
									: 'border-green-400 bg-green-50 text-green-800'
							}`}>
								<input
									type='checkbox'
									checked={addAnshinFee}
									onChange={(e) => setAddAnshinFee(e.target.checked)}
									className='w-4 h-4'
								/>
								{Number(anshinSuggestion.amountJpy) > 0
									? '⚠️ สินค้านี้ส่งตรวจได้ มีค่าบริการ — ติ๊ก = ส่งตรวจและเก็บค่าบริการ'
									: '✅ สินค้านี้ส่งตรวจได้ฟรี — ติ๊ก = ส่งตรวจ'}
							</label>
							{addAnshinFee && (
								<div className='flex items-center gap-1'>
									<input
										type='text'
										name='anshinFeeJpy'
										value={anshinSuggestion.amountJpy}
										readOnly
										disabled
										className='w-20 border-2 border-gray-200 py-1 pl-2 rounded-md bg-gray-100 text-gray-700 cursor-not-allowed'
									/>
									<span className='text-sm text-gray-700'>เยน</span>
								</div>
							)}
						</div>
					)}
				</div>
			</div>

			{/* Blacklist Warning Modal */}
			<BlacklistWarningModal
				isOpen={currentBlacklistWarning !== null}
				onClose={handleCloseBlacklistWarning}
				message={currentBlacklistWarning}
			/>

			{/* Same Contact Warning Modal */}
			<BlacklistWarningModal
				isOpen={sameContactWarningMessage !== null}
				onClose={handleCloseSameContactWarning}
				message={sameContactWarningMessage}
			/>

	<ConfirmImageModal 
		isOpen={showConfirmImageModal}
		onRequestClose={() => setShowConfirmImageModal(false)}
		onConfirm={handleFinalSave}
		onSkip={() => handleFinalSave(null, null)}
		isHandCarry={order.isHandCarry}
		website={order.website}
		expectedSmileName={
			profile?.smileName && profile?.smileOrderNo != null
				? `${profile.smileName}${profile.smileOrderNo + 1}`
				: null
		}
		suggestedWarehouse={routingSuggestion?.warehouse ?? null}
		expectedItemNo={extractItemNoFromUrl(order.url)}
		skipSmileNameOcrCheck={true}
	/>
			
		{/* Purchase Image Warning Modal - Shows AFTER successful save */}
		<Dialog 
			open={postcodeWarning !== null} 
			onClose={() => {}}
			maxWidth="sm"
			fullWidth
		>
		<DialogTitle sx={{
			backgroundColor: (postcodeWarning?.itemNoMismatch || (!postcodeWarning?.isValid && !postcodeWarning?.cannotValidate)) ? '#7f1d1d' : '#92400e',
			color: 'white'
		}}>
			<div className="flex items-center gap-2">
				<span className="text-2xl">{(postcodeWarning?.itemNoMismatch || (!postcodeWarning?.isValid && !postcodeWarning?.cannotValidate)) ? '🚨' : '⚠️'}</span>
				<span className="font-bold">
					{postcodeWarning?.itemNoMismatch
						? 'ติดต่อคุณเกน เพื่อแก้ไข'
						: 'ตรวจพบข้อมูลในรูปยืนยันไม่ตรง'}
				</span>
			</div>
		</DialogTitle>
			<DialogContent sx={{ pt: 2 }}>
				{/* Tier 1: item ID mismatch */}
				{postcodeWarning?.itemNoMismatch && (
					<div className="mt-3 p-3 bg-red-50 rounded-lg border-2 border-red-600">
						<p className="text-red-900 font-bold">❌ รหัสสินค้าในรูปไม่ตรง</p>
						<p className="text-red-800 text-sm mt-1">รหัสในรูป: <strong>{postcodeWarning.extractedItemNo}</strong></p>
						<p className="text-red-800 text-sm">รหัสที่ควรเป็น: <strong>{postcodeWarning.expectedItemNo}</strong></p>
						<p className="text-gray-600 mt-2 text-sm break-all">
							<a href={postcodeWarning.expectedOrderUrl || order.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
								{postcodeWarning.expectedOrderUrl || order.url}
							</a>
						</p>
					</div>
				)}
		{/* Tier 1: postcode mismatch — show when postcode was found but wrong */}
			{postcodeWarning?.message && !postcodeWarning?.isValid && !postcodeWarning?.cannotValidate && (
				<div className="mt-3 p-3 bg-red-50 rounded-lg border-2 border-red-500">
					<pre className="text-red-800 whitespace-pre-wrap text-sm font-bold">
						{postcodeWarning.message}
					</pre>
				</div>
			)}
				{/* Tier 2: order number mismatch — softer amber */}
				{postcodeWarning?.nameMismatchMessage && (
					<div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-400">
						<p className="text-amber-800 text-sm font-medium">
							⚠️ {postcodeWarning.nameMismatchMessage}
						</p>
					</div>
				)}
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
				<Button 
					onClick={() => setPostcodeWarning(null)}
					variant="contained"
					color={(postcodeWarning?.itemNoMismatch || (!postcodeWarning?.isValid && !postcodeWarning?.cannotValidate)) ? 'error' : 'warning'}
					size="large"
					sx={{ minWidth: 180 }}
				>
					รับทราบ
				</Button>
			</DialogActions>
		</Dialog>
			<ShippingTypeDialog
				open={showShippingTypeDialog}
				onClose={handleShippingTypeCancel}
				onSelect={handleShippingTypeSelect}
				hasEverHadAirOrder={!!profile?.hasEverHadAirOrder}
			/>
			<OrdererConfirmModal
				ordererMatch={pendingOrdererMatch}
				orderByOptions={orderByOptions}
				onConfirmed={() => setPendingOrdererMatch(null)}
			/>
			<Portal>
				<Backdrop open={isCheckingOrderer} sx={{ color: '#fff', zIndex: 99999, flexDirection: 'column', gap: 2 }}>
					<CircularProgress color="inherit" />
					<Typography>กำลังบันทึก / ตรวจสอบ orderer...</Typography>
				</Backdrop>
			</Portal>
    </div>
  )
}

export default OrderForm